import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { PaymentFrequencyEnum } from '@nxsys/constants/AppEnums';
import { PSR_SUBMIT_TYPE } from '@nxsys/constants/psr';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { GetPayrollToSubmitPSRParams } from '@nxsys/store/slices/revenueFilings/types';
import { debounce, toNumber } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEmployerSearchScope } from '@nxsys/hooks/useEmployerSearchScope';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type ActionsHeaderProps = {
  type: (typeof PSR_SUBMIT_TYPE)[keyof typeof PSR_SUBMIT_TYPE];
  onFilter: (params: GetPayrollToSubmitPSRParams) => void;
  onSubmitLeaver?: () => void;
  onNext?: () => void;
};

type FilterValues = {
  payFrequency: PayFrequencyValue | null;
  taxPeriod: number | null;
  company: OptionType | null;
};

export const ActionsHeader = ({
  type,
  onFilter,
  onSubmitLeaver,
  onNext,
}: ActionsHeaderProps) => {
  const { messages, formatMessage } = useTypedIntl();

  const employerSearchScope = useEmployerSearchScope();
  const employerLabels = useEmployerFieldLabels();

  const { payrollToSubmitPSR, leaverToSubmitPSR } = useSelector(
    (state: AppState) => state.revenueStore,
  );

  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState<FilterValues>({
    payFrequency: null,
    taxPeriod: null,
    company: null,
  });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      onFilter({
        ...(type === PSR_SUBMIT_TYPE.NORMAL
          ? {
              ...payrollToSubmitPSR.queryParams,
            }
          : {
              ...leaverToSubmitPSR.queryParams,
            }),
        page: 0,
        search,
      });
    }, 500),
    [payrollToSubmitPSR.queryParams, leaverToSubmitPSR.queryParams],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleChangeFilter = (
    key: keyof FilterValues,
    value: FilterValues[keyof FilterValues],
  ) => {
    const newFilterValues: FilterValues = {
      ...filterValues,
      [key]: value,
    };
    if (key === 'payFrequency' && !value) {
      newFilterValues.taxPeriod = null;
    }
    const taxPeriod = newFilterValues.taxPeriod
      ? toNumber(newFilterValues.taxPeriod)
      : undefined;
    setFilterValues(newFilterValues);
    onFilter({
      ...(type === PSR_SUBMIT_TYPE.NORMAL
        ? {
            ...payrollToSubmitPSR.queryParams,
          }
        : {
            ...leaverToSubmitPSR.queryParams,
          }),
      page: 0,
      payFrequency: newFilterValues.payFrequency,
      ...(newFilterValues.payFrequency === PaymentFrequencyEnum.MONTHLY
        ? { taxMonth: taxPeriod }
        : { taxPeriod }),
      companyRefs: newFilterValues.company
        ? [newFilterValues.company?.value]
        : undefined,
    });
  };

  const ableToNextStep = useMemo(
    () =>
      filterValues.payFrequency &&
      filterValues.taxPeriod &&
      filterValues.company,
    [filterValues],
  );

  if (type === PSR_SUBMIT_TYPE.NORMAL) {
    return (
      <div className="flex items-center justify-between gap-2 w-full flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <AppPayFrequencySelect
            placeholder={messages['common.payFrequency']}
            value={filterValues?.payFrequency}
            onFilter={(e) => handleChangeFilter('payFrequency', e.payFrequency)}
            allowClear={true}
            className="!w-full md:!w-[280px] sm:!w-full"
          />
          <TaxWeekMonthSelect
            placeholder={messages['common.taxPeriod']}
            onFilter={(e) => handleChangeFilter('taxPeriod', e.taxPeriod)}
            value={filterValues?.taxPeriod}
            paymentFrequency={filterValues?.payFrequency ?? 'WEEKLY'}
            disabled={!filterValues?.payFrequency}
            allowClear={true}
            className="!w-full md:!w-[280px] sm:!w-full"
          />
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            value={filterValues?.company}
            placeholder={employerLabels.filterPlaceholder}
            onChangeValue={(e) =>
              handleChangeFilter('company', e as OptionType)
            }
            paramFilter={employerSearchScope}
            allowClear={true}
            className="!w-full md:!w-[280px] sm:!w-full"
          />
          <AppButton
            onClick={onNext}
            name={messages['common.nextStep']}
            disabled={!ableToNextStep}
          />
        </div>
        <AppInputSearch
          value={searchValue}
          onChange={handleSearchChange}
          className="!w-full md:!w-[280px] sm:!w-full"
        />
      </div>
    );
  } else {
    return (
      <div className="flex items-center justify-between gap-2 w-full">
        <AppInputSearch
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={formatMessage(
            { id: 'common.searchBy' },
            { key: messages['common.name'].toLowerCase() },
          )}
        />
        <AppButton onClick={onSubmitLeaver} name={messages['common.submit']} />
      </div>
    );
  }
};
